import { DefaultTFuncReturn } from "i18next";
import i18n from "~/translations/i18n";
import { toast } from "~/components/toast";
import { IToastOptions, ToastId, ToastType } from "~/components/toast/types";
import { triggerShowPlansModal } from "~/helpers/customEvents";
import { errorMessage } from "~/api/errors";

export type ToastMessage = {
  loading?:
    | DefaultTFuncReturn
    | { body: React.ReactNode; options?: IToastOptions };
  success?:
    | DefaultTFuncReturn
    | { body: React.ReactNode; options?: IToastOptions };
  error?:
    | DefaultTFuncReturn
    | { body: React.ReactNode; options?: IToastOptions };
};

type ResponseWithError<T = any> =
  | { response: null; errors: string[] }
  | { response: T; errors: null };

enum ErrorCode {
  REQUIRES_UPGRADE = 1,
}

const fromToastMessage = (
  type: ToastType,
  message?:
    | DefaultTFuncReturn
    | { body: React.ReactNode; options?: IToastOptions },
  toastId?: ToastId
) => {
  if (!message) {
    if (toastId) {
      toast.dismiss(toastId);
    }

    return;
  }

  if (typeof message === "string") {
    return toast.updateOrCreate(toastId, {
      type,
      render: message,
    });
  }

  return toast.updateOrCreate(toastId, {
    type,
    render: message.body,
    ...message.options,
  });
};

// `String(value)` throws for values without a usable toString (for example
// `Object.create(null)`). This runs inside a catch block, so it must never throw.
const safeString = (value: unknown, fallback: string): string => {
  try {
    return String(value);
  } catch {
    return fallback;
  }
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const handleErrors = async <T = any>(
  promise: () => Promise<T>,
  toasts: ToastMessage
): Promise<ResponseWithError<T>> => {
  const toastId = fromToastMessage("info", toasts.loading);

  try {
    const response = await promise();
    if (toasts.success) fromToastMessage("success", toasts.success, toastId);
    return { response, errors: null };
  } catch (error: any) {
    // Reads the first message of each field of a DRF-style field map. Any other
    // body shape (string, null, { errors: "text" }, ...) used to throw or yield a
    // single character here; it now goes through the shared helper, which never throws.
    const handleFormErrors = (errorData: unknown) => {
      if (errorData === null || errorData === undefined || errorData === "") {
        return [];
      }

      const isFieldMap =
        typeof errorData === "object" &&
        !Array.isArray(errorData) &&
        Object.values(errorData as object).every(Array.isArray);

      if (!isFieldMap) {
        const message = errorMessage(error, "");
        return message ? [message] : [];
      }

      const fields = errorData as Record<string, string[]>;
      const newErrors: Record<string, string> = {};
      Object.keys(fields).forEach((key) => {
        newErrors[key] = fields[key][0];
      });
      return Object.values(newErrors);
    };

    let errors: string[] | null = [];
    const errorData = error?.response?.status;

    if (errorData === 429) {
      const message = i18n.t("auth.rate_limited");
      fromToastMessage("error", message, toastId);
      return { response: null, errors: [message] };
    }

    if (
      errorData === 400 ||
      errorData === 402 ||
      errorData === 404 ||
      errorData === 422
    ) {
      const data = error.response?.data;
      // A v1 validation envelope carries a generic `message` ("Validation failed") and the
      // real, field-level messages in `details`. Read those instead of the generic text.
      const validationDetails =
        data?.code === "validation_failed" && isPlainObject(data.details)
          ? data.details
          : undefined;
      // `error` can also be an object ({ current_password: [...] }); only a string is a
      // message. Anything else goes through the shared parser below.
      const raw = validationDetails
        ? undefined
        : data?.message || data?.error || data?.detail;
      const message = typeof raw === "string" ? raw : undefined;

      if (error.response?.data?.error_code === ErrorCode.REQUIRES_UPGRADE) {
        triggerShowPlansModal();
      }

      if (message) {
        fromToastMessage("error", message, toastId);

        return { response: null, errors: [message] };
      }

      if (/text\/html/.test(error?.response?.headers?.["content-type"])) {
        fromToastMessage("error", toasts.error, toastId);
      } else {
        errors = handleFormErrors(validationDetails ?? error?.response?.data);

        if (errors.length) {
          fromToastMessage("error", errors[0], toastId);
        }
      }
    } else {
      fromToastMessage("error", toasts.error, toastId);
    }

    return {
      response: null,
      errors: errors.length
        ? errors
        : [safeString(error, "Something went wrong")],
    };
  }
};

export default handleErrors;
